/**
 * Where a landmark actually is on screen.
 *
 * MediaPipe reports landmarks normalised to the video frame: 0..1 across, 0..1
 * down, in the camera's own aspect ratio. Getting from there to a pixel on the
 * page means undoing three things the browser did on the way, and every one of
 * them is silent when wrong — the type simply sits somewhere near the face, and
 * "near" is indistinguishable from a bad landmark index.
 *
 * **`object-fit: cover`.** A full-bleed video is scaled to fill the box and
 * cropped on whichever axis is too long. A 4:3 camera in a 16:9 window loses the
 * top and bottom of the picture, so a landmark at y=0.5 is *not* at the middle of
 * the box. This is the failure that would put the anchor on the chin in portrait
 * and on the nose in landscape.
 *
 * **The mirror.** The feed is flipped, because a face that moves the wrong way
 * when you lean is unusable — the lab has done this since Phase 1. The landmarks
 * are not flipped, so anything drawn from them has to be, or the text pours out
 * of the wrong side of the mouth and follows the head backwards.
 *
 * **The layer.** The anchor is measured in the video's box and consumed in some
 * other element's, and those two boxes are not the same rectangle — see
 * `boxFor`, which is the one this project got wrong for a whole phase.
 *
 * All three are pure functions here, and tested, because they are geometry
 * rather than rendering and there is no reason to find out about them by
 * looking.
 */

/**
 * The rectangle a video actually occupies under `object-fit: cover`.
 *
 * Returned in box coordinates, so `x`/`y` are negative on the cropped axis —
 * that is the point, it is what puts a landmark back where the eye sees it.
 *
 * @param {number} videoW intrinsic width
 * @param {number} videoH intrinsic height
 * @param {number} boxW
 * @param {number} boxH
 * @returns {?{x: number, y: number, w: number, h: number, scale: number}}
 */
export function coverRect(videoW, videoH, boxW, boxH) {
  if (!(videoW > 0 && videoH > 0 && boxW > 0 && boxH > 0)) return null;
  const scale = Math.max(boxW / videoW, boxH / videoH);
  const w = videoW * scale;
  const h = videoH * scale;
  return { x: (boxW - w) / 2, y: (boxH - h) / 2, w, h, scale };
}

/**
 * The video's box, expressed in the coordinates of a layer drawn over it.
 *
 * The third thing that is silent when wrong, and the one that was. The video is
 * fixed to the window; the layer the anchor is *consumed* in is not, and a layer
 * that happens to start at the same corner still has its own size. Measuring the
 * cover fit against the layer rather than against the video puts the anchor a
 * few hundred pixels from the mouth while every landmark index is correct — so
 * `coverRect` is given the video's own box, and `dx`/`dy` carry the difference
 * between the two origins instead of an assumption that there is none.
 *
 * Rect-likes in, plain numbers out, so this stays testable without a DOM.
 *
 * @param {{left: number, top: number, width: number, height: number}} videoRect
 * @param {{left: number, top: number}} layerRect
 * @returns {{boxW: number, boxH: number, dx: number, dy: number}}
 */
export function boxFor(videoRect, layerRect) {
  return {
    boxW: videoRect.width,
    boxH: videoRect.height,
    dx: videoRect.left - layerRect.left,
    dy: videoRect.top - layerRect.top,
  };
}

/**
 * One normalised landmark to a pixel in the box.
 *
 * `dx`/`dy` are applied last, after the mirror, because the mirror is a property
 * of the video and the offset is a property of where the layer sits over it.
 * Flipping an already-offset x would reflect the layer's origin as well.
 *
 * @param {{x: number, y: number}} point normalised 0..1
 * @param {{x: number, y: number, w: number, h: number}} rect from coverRect
 * @param {object} [o]
 * @param {boolean} [o.mirrored] the feed is flipped horizontally
 * @param {number} [o.boxW] required when mirrored
 * @param {number} [o.dx] video box origin minus layer origin, from boxFor
 * @param {number} [o.dy]
 */
export function toScreen(point, rect, { mirrored = false, boxW = 0, dx = 0, dy = 0 } = {}) {
  const x = rect.x + point.x * rect.w;
  const y = rect.y + point.y * rect.h;
  return { x: (mirrored ? boxW - x : x) + dx, y: y + dy };
}

/**
 * Where the text should come out of, and how big it should be.
 *
 * The anchor is the centre of the inner lip opening rather than a corner: a
 * corner swings a long way on a smile, and the text would lurch sideways every
 * time the speaker widened their mouth on an "eee".
 *
 * `iod` comes back in screen pixels because it is the scale reference for
 * everything else — the same inter-ocular distance the mouth features are
 * normalised by in Phase 1, which is what lets the type size mean "relative to
 * this face" rather than "relative to this window". It is a distance between two
 * `toScreen` results, so the layer offset cancels out of it and only the anchor
 * itself moves — which is why `o` can be passed straight through.
 *
 * @param {object[]} landmarks
 * @param {object} idx config.vision.idx
 * @param {object} rect from coverRect
 * @param {object} [o] passed through to toScreen
 * @returns {?{x: number, y: number, iod: number}}
 */
export function mouthAnchor(landmarks, idx, rect, o = {}) {
  if (!landmarks || !rect) return null;

  const upper = landmarks[idx.lipUpperInner];
  const lower = landmarks[idx.lipLowerInner];
  const eyeL = landmarks[idx.eyeL];
  const eyeR = landmarks[idx.eyeR];
  if (!upper || !lower || !eyeL || !eyeR) return null;

  const mouth = toScreen({ x: (upper.x + lower.x) / 2, y: (upper.y + lower.y) / 2 }, rect, o);
  const a = toScreen(eyeL, rect, o);
  const b = toScreen(eyeR, rect, o);
  const iod = Math.hypot(a.x - b.x, a.y - b.y);
  if (!(iod > 0)) return null;

  return { x: mouth.x, y: mouth.y, iod };
}

/**
 * An eased 2-D anchor.
 *
 * Landmarks jitter by a pixel or two on a face that is not moving, and type
 * pinned straight to them shimmers in a way that reads as a rendering bug. This
 * is a plain exponential follower with one useful exception: it snaps rather
 * than eases the first time it is given a position, so the text does not fly in
 * from the corner of the screen when the camera opens.
 */
export class SmoothAnchor {
  constructor() {
    this.x = 0;
    this.y = 0;
    this.iod = 0;
    this.ready = false;
    /** Consecutive readings rejected as impossible. */
    this.rejected = 0;
  }

  /**
   * @param {?{x: number, y: number, iod: number}} target null when there is no face
   * @param {number} ease 0..1 per frame
   * @param {object} [o]
   * @param {number} [o.jump] furthest a face may move in one reading, ×iod. 0 disables.
   * @param {number} [o.giveUp] readings rejected before the jump is believed
   */
  update(target, ease, { jump = 0, giveUp = 8 } = {}) {
    if (!target) {
      // Deliberately keeps the last position rather than resetting. A face lost
      // for three frames should not send the line to the origin and back.
      return this.ready ? this : null;
    }
    if (!this.ready) {
      this.x = target.x;
      this.y = target.y;
      this.iod = target.iod;
      this.ready = true;
      this.rejected = 0;
      return this;
    }

    // Landmarks blow up. Not rarely, either — on a measured desktop take 27.8%
    // of frames put the inner lips further apart than a whole face, in runs of
    // up to 125 frames, and the anchor followed every one of them. Easing toward
    // a garbage reading is what "the crosshair keeps wandering off my mouth" is,
    // and it looks exactly like a coordinate bug.
    //
    // A face cannot cross more than a fraction of an inter-ocular distance
    // between two camera frames, so a reading that says otherwise is not a
    // measurement. The give-up counter is what keeps this from being a trap: a
    // genuinely fast move, a cut, or a second person stepping in front of the
    // camera would be rejected forever otherwise, so after `giveUp` consecutive
    // refusals the anchor accepts that the world really did change and snaps.
    if (jump > 0) {
      const far = Math.hypot(target.x - this.x, target.y - this.y) > jump * this.iod;
      if (far && this.rejected < giveUp) {
        this.rejected++;
        return this;
      }
      if (far) {
        this.x = target.x;
        this.y = target.y;
        this.iod = target.iod;
        this.rejected = 0;
        return this;
      }
    }
    this.rejected = 0;

    const k = Math.min(1, Math.max(0, ease));
    this.x += (target.x - this.x) * k;
    this.y += (target.y - this.y) * k;
    this.iod += (target.iod - this.iod) * k;
    return this;
  }

  reset() {
    this.ready = false;
    this.rejected = 0;
  }
}
